import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface SelectedInstanceType {
  data: string;
  variableName: string;
  responseSelection: {
    bodySniff?: JSON;
    xPath: string;
    jsonPath: string;
    responseMatch: string;
    matchType: string;
    invariant: boolean;
    isPrivateInformation?: boolean;
    description?: string;
    requestIndex?: number;
    hash?: string;
    order?: number;
  };
}

interface SelectedInstances {
  selectedInstances: SelectedInstanceType[];
  paramsAdded: Record<string, string>;
}

const initialState: SelectedInstances = {
  selectedInstances: [],
  paramsAdded: {},
};

const selectedInstancesSlice = createSlice({
  name: 'selectedInstances',
  initialState,
  reducers: {
    updateSelectedInstances(state, action: PayloadAction<SelectedInstanceType>) {
      state.selectedInstances = [...state.selectedInstances, action.payload];
    },
    deleteSelectedInstance(state, action: PayloadAction<number>) {
      if (action.payload < state.selectedInstances.length && action.payload >= 0) {
        state.selectedInstances.splice(action.payload, 1);
      }
    },
    // update response selection of a particular instance
    updateResponseSelection(
      state,
      action: PayloadAction<{
        index: number;
        responseSelection: SelectedInstanceType['responseSelection'];
      }>
    ) {
      if (
        action.payload.index < state.selectedInstances.length &&
        action.payload.index >= 0
      ) {
        state.selectedInstances[action.payload.index].responseSelection =
          action.payload.responseSelection;
      }
    },
    resetSelectedInstancesForRequest(state, action: PayloadAction<number>) {
      const requestIndex = action.payload;
      state.selectedInstances = state.selectedInstances.filter(
        (instance) => instance.responseSelection.requestIndex !== requestIndex
      );
    },
    resetSelectedInstances() {
      return { ...initialState };
    },
    editVariableName(state, action: PayloadAction<{ value: string; index: number }>) {
      const { index, value } = action.payload;
      const currentInstance = state.selectedInstances[index];

      if (currentInstance) {
        const oldVariableName = currentInstance.variableName;
        const updatedInstance = {
          ...currentInstance,
          variableName: value,
          responseSelection: {
            ...currentInstance.responseSelection,
            responseMatch: currentInstance.responseSelection.responseMatch.replace(
              new RegExp(`{{${oldVariableName}}}`, 'g'),
              `{{${value}}}`
            ),
          },
        };

        state.selectedInstances[index] = updatedInstance;
      }
    },
    setSelectedInstances(state, action: PayloadAction<SelectedInstanceType[]>) {
      state.selectedInstances = action.payload;
    },
    setParamsAdded(state, action: PayloadAction<Record<string, string>>) {
      state.paramsAdded = action.payload;
    },
    resetParamsAdded(state) {
      state.paramsAdded = {};
    },
  },
});

export const {
  updateSelectedInstances,
  deleteSelectedInstance,
  updateResponseSelection,
  resetSelectedInstances,
  resetSelectedInstancesForRequest,
  setSelectedInstances,
  editVariableName,
  setParamsAdded,
  resetParamsAdded,
} = selectedInstancesSlice.actions;

export default selectedInstancesSlice.reducer;
